import type { UpdatedProductKey } from '@cartwhl/common/stores/updatedProductStore';

export type PricingTabSlug =
  | 'team-plans'
  | 'individual-plans'
  | 'platform-plans';

export type BillingMode = 'monthly' | 'yearly';

export type PricingPlanAccent = 'neutral' | 'primary' | 'success';

export interface PricingTabLink {
  slug: PricingTabSlug;
  label: string;
  shortLabel: string;
}

export interface PricingCardConfig {
  productKey: UpdatedProductKey;
  title: string;
  description: string;
  creditsValue: string;
  creditsPeriodLabel: string;
  periodLabel: Record<BillingMode, string>;
  yearlyDisplayPrice?: string;
  yearlyTotalLabel?: string;
  accent: PricingPlanAccent;
}

export interface PricingMatrixColumn {
  label: string;
  accent: PricingPlanAccent;
}

export type PricingMatrixValueTone = 'primary' | 'secondary' | 'tertiary';

export interface PricingMatrixValueItemConfig {
  label: string;
  tone?: PricingMatrixValueTone;
  icon?: 'lock';
}

export type PricingMatrixValueItem = string | PricingMatrixValueItemConfig;

export interface PricingMatrixSection {
  label: string;
  values: PricingMatrixValueItem[][];
  highlighted?: boolean;
}

export interface PricingMatrixConfig {
  title: string;
  columns: PricingMatrixColumn[];
  sections: PricingMatrixSection[];
}

export interface PlatformFeature {
  title: string;
  body: string;
}

export interface PlatformDetailSection {
  label: string;
  mobileLabel?: string;
  values: string[];
  highlighted?: boolean;
}

export interface PlatformConfig {
  titlePrefix: string;
  titleSuffix: string;
  features: PlatformFeature[];
  detailTitle: string;
  detailLead: string;
  detailLinkLabel: string;
  detailTail: string;
  detailListLead?: string;
  sections: PlatformDetailSection[];
}

export interface PricingFooterCtaConfig {
  title: string;
  body: string;
  label: string;
  tone: 'green' | 'purple';
}

export interface PricingTabConfig {
  slug: PricingTabSlug;
  label: string;
  shortLabel: string;
  tabsMaxWidthClassName: string;
  showBillingToggle: boolean;
  showCreditsExplainer: boolean;
  featuredProductKey?: UpdatedProductKey;
  cards?: PricingCardConfig[];
  matrix?: PricingMatrixConfig;
  platform?: PlatformConfig;
  footerCta: PricingFooterCtaConfig;
}

const appList = [
  'Comic',
  'Motion Generation',
  'Motion Editor',
  'Mascot',
  'Pose',
  'Character Generation',
  'Motion Library',
  'Cartographer',
];

const teamCollaborationList = [
  'Pooled credits',
  'Team library',
  'Team management controls',
];

const teamScaleSupportList = [
  'Dedicated account manager',
  'White-glove onboarding',
  '12-hr SLA',
  '99.5% uptime SLA',
  'Dedicated Chat channel',
  'Quarterly check-ins',
  'Early beta access to all features',
];

export const pricingTabs: PricingTabLink[] = [
  {
    slug: 'individual-plans',
    label: 'Individual plans',
    shortLabel: 'Individuals',
  },
  {
    slug: 'team-plans',
    label: 'Team plans',
    shortLabel: 'Teams',
  },
  {
    slug: 'platform-plans',
    label: 'Platform plans',
    shortLabel: 'Platforms',
  },
];

export const pricingTabConfigs: Record<PricingTabSlug, PricingTabConfig> = {
  'team-plans': {
    slug: 'team-plans',
    label: 'Team plans',
    shortLabel: 'Teams',
    tabsMaxWidthClassName: 'lg:mx-auto lg:max-w-[832px]',
    showBillingToggle: true,
    showCreditsExplainer: true,
    featuredProductKey: 'team_pro',
    cards: [
      {
        productKey: 'team_standard',
        title: 'Team Standard',
        description:
          'For small teams managing shared usage and scaling generation across members',
        creditsValue: '500 credits',
        creditsPeriodLabel: 'per seat pooled monthly',
        periodLabel: {
          monthly: 'per mo/seat',
          yearly: 'mo/seat',
        },
        yearlyDisplayPrice: '57',
        yearlyTotalLabel: '690',
        accent: 'neutral',
      },
      {
        productKey: 'team_pro',
        title: 'Team Pro',
        description:
          'For teams running production pipelines with API-driven workflows, automation, plugins, and scalable generation',
        creditsValue: '1500 credits',
        creditsPeriodLabel: 'per seat pooled monthly',
        periodLabel: {
          monthly: 'per mo/seat',
          yearly: 'mo/seat',
        },
        yearlyDisplayPrice: '124',
        yearlyTotalLabel: '1488',
        accent: 'primary',
      },
      {
        productKey: 'team_scale',
        title: 'Team Scale',
        description:
          'For high-volume teams optimizing for speed, efficiency, and cost at scale',
        creditsValue: '4000 credits',
        creditsPeriodLabel: 'per seat pooled monthly',
        periodLabel: {
          monthly: 'per mo/seat',
          yearly: 'mo/seat',
        },
        yearlyDisplayPrice: '249',
        yearlyTotalLabel: '2988',
        accent: 'success',
      },
    ],
    matrix: {
      title: 'Team plan details',
      columns: [
        { label: 'Standard', accent: 'neutral' },
        { label: 'Pro', accent: 'primary' },
        { label: 'Scale', accent: 'success' },
      ],
      sections: [
        {
          label: 'Apps',
          values: [appList, appList, appList],
        },
        {
          label: 'Collaboration tools',
          values: [
            teamCollaborationList,
            teamCollaborationList,
            teamCollaborationList,
          ],
        },
        {
          label: 'API',
          highlighted: true,
          values: [
            [
              {
                label: 'Pro & Scale plans only',
                tone: 'tertiary',
                icon: 'lock',
              },
            ],
            [
              'Capture in batches',
              'Generate in batches',
              'Automate pipelines',
              'Scalable systems',
              'Plugins',
              '3 concurrent batch generations per seat',
              '50 motions max per batch',
            ],
            [
              'Capture in batches',
              'Generate in batches',
              'Automate pipelines',
              'Scalable systems',
              'Plugins',
              '10 concurrent batch generations per seat',
              '100 motions max per batch',
            ],
          ],
        },
        {
          label: 'Support',
          values: [
            ['Email & chat support', '(48hr SLA)'],
            ['Email & chat support', '(24hr SLA)'],
            teamScaleSupportList,
          ],
        },
      ],
    },
    footerCta: {
      title: 'Ready for your free trial?',
      body: 'We offer a 7-day free trial so you can evaluate whether Cartwheel meets your creation needs and accelerates your production workflows.',
      label: 'Select your plan',
      tone: 'green',
    },
  },
  'individual-plans': {
    slug: 'individual-plans',
    label: 'Individual plans',
    shortLabel: 'Individuals',
    tabsMaxWidthClassName: 'lg:mx-auto lg:max-w-[832px]',
    showBillingToggle: true,
    showCreditsExplainer: true,
    featuredProductKey: 'individual_pro',
    cards: [
      {
        productKey: 'individual_basic',
        title: 'Individual Basic',
        description:
          'For solo creators that produce content in Canvas and through our suite of apps',
        creditsValue: '200 credits',
        creditsPeriodLabel: 'per month',
        periodLabel: {
          monthly: '/mo',
          yearly: '/mo',
        },
        accent: 'neutral',
      },
      {
        productKey: 'individual_pro',
        title: 'Individual Pro',
        description:
          'For creators who want more credits, plugin access, and API access for automation and programmatic workflows',
        creditsValue: '500 credits',
        creditsPeriodLabel: 'per month',
        periodLabel: {
          monthly: '/mo',
          yearly: '/mo',
        },
        accent: 'primary',
      },
    ],
    matrix: {
      title: 'Individual plan details',
      columns: [
        { label: 'Basic', accent: 'neutral' },
        { label: 'Pro', accent: 'primary' },
      ],
      sections: [
        {
          label: 'Apps',
          values: [appList, appList],
        },
        {
          label: 'Collaboration tools',
          values: [
            [
              {
                label: 'Team plans only',
                tone: 'tertiary',
                icon: 'lock',
              },
            ],
            [
              {
                label: 'Team plans only',
                tone: 'tertiary',
                icon: 'lock',
              },
            ],
          ],
        },
        {
          label: 'API',
          highlighted: true,
          values: [
            [
              {
                label: 'Pro & Scale plans only',
                tone: 'tertiary',
                icon: 'lock',
              },
            ],
            [
              'Capture in batches',
              'Generate in batches',
              'Automate pipelines',
              'Scalable systems',
              'Plugins',
              '1 concurrent batch generation',
              'Max batch size 30',
            ],
          ],
        },
        {
          label: 'Support',
          values: [
            ['Email & chat support', '(48hr SLA)'],
            ['Email & chat support', '(48hr SLA)'],
          ],
        },
      ],
    },
    footerCta: {
      title: 'Ready for your free trial?',
      body: 'We offer a 7-day free trial so you can evaluate whether Cartwheel meets your creation needs and accelerates your production workflows.',
      label: 'Select your plan',
      tone: 'green',
    },
  },
  'platform-plans': {
    slug: 'platform-plans',
    label: 'Platform plans',
    shortLabel: 'Platforms',
    tabsMaxWidthClassName: 'lg:mx-auto lg:max-w-[832px]',
    showBillingToggle: false,
    showCreditsExplainer: true,
    platform: {
      titlePrefix: 'Platforms',
      titleSuffix:
        'allow you to embed motion generation in your product or system.',
      features: [
        {
          title: 'For your apps & games',
          body: 'Generate and stream character motion natively inside your app or game, giving your characters unique style, while allowing your users to have their own custom motions and stay immersed.',
        },
        {
          title: 'Expand your systems toolset',
          body: 'Add generative motion as a native capability in your tools, giving your team the ability to animate without switching context. Make animation a feature of your platform, not a separate step.',
        },
      ],
      detailTitle: 'Platform plan details',
      detailLead: 'The Platform plan ',
      detailLinkLabel:
        'includes everything in the Team Scale plan at a base level',
      detailTail:
        '. Concurrent jobs and batch sizes are custom designed to your unique use case.',
      detailListLead: 'The Platform plan includes:',
      sections: [
        {
          label: 'Cartwheel Web Apps',
          mobileLabel: 'Cartwheel web apps',
          values: appList,
        },
        {
          label: 'Collaboration tools',
          values: teamCollaborationList,
        },
        {
          label: 'API',
          highlighted: true,
          values: [
            'Capture in batches',
            'Generate in batches',
            'Automate pipelines',
            'Scalable systems',
            'Plugins',
            'Custom concurrent batch generations',
            'Custom maximum batch size',
          ],
        },
        {
          label: 'Support',
          values: ['Custom support options available'],
        },
      ],
    },
    footerCta: {
      title: 'Want to learn more?',
      body: 'Contact our sales team for a demo to see how Platforms can be customized to fit your needs.',
      label: 'Get in touch',
      tone: 'purple',
    },
  },
};
